import React from 'react';
import { useTheme } from '../context/ThemeContext';
import { useProjectStore } from '../store/useProjectStore';
import { VSM_BLUEPRINT_MAP } from './VsmIconsBlueprint';

/**
 * VSM Canvas Shapes — modo FACTORY usa PNGs de /assets/vsm/.
 * Modo OFFICE não usa este componente (isolamento total).
 */

interface VsmShapeProps {
  width: number;
  height: number;
  label?: string;
  selected?: boolean;
  color?: string;
  isDark?: boolean;
  isGhost?: boolean;
  style?: React.CSSProperties;
}

/**
 * Mapa: vsmIcon key → path do SVG em /assets/vsm/
 * Ícones sem SVG usam fallback abaixo.
 */
const VSM_PNG_MAP: Record<string, string> = {
  processo:      '/assets/vsm/processo.svg',
  operador:      '/assets/vsm/operador.svg',
  databox:       '/assets/vsm/databox.svg',
  comunicacao:   '/assets/vsm/comunicacao.svg',
  info_manual:   '/assets/vsm/info_manual.svg',
  info_eletro:   '/assets/vsm/info_eletro.svg',
  empurrado:     '/assets/vsm/empurrado.svg',
  puxado:        '/assets/vsm/puxado.svg',
  fifo:          '/assets/vsm/fifo.svg',
  supermercado:  '/assets/vsm/supermercado.svg',
  fornecedor:    '/assets/vsm/externo.svg',
  cliente:       '/assets/vsm/externo.svg',
  externo:       '/assets/vsm/externo.svg',
  kanban_prod:   '/assets/vsm/kanban_prod.svg',
  kanban_ret:    '/assets/vsm/kanban_ret.svg',
  kanban_lote:   '/assets/vsm/kanban_lote.svg',
  kanban_posto:  '/assets/vsm/kanban_posto.svg',
  kanban_sinal:  '/assets/vsm/kanban_sinal.svg',
  nivelamento:   '/assets/vsm/nivelamento.svg',
  vaver:         '/assets/vsm/vaver.svg',
  kaizen:        '/assets/vsm/kaizen.svg',
  estoque:       '/assets/vsm/estoque.svg',
  produto:       '/assets/vsm/produto.svg',
  entrega:       '/assets/vsm/entrega.svg',
  pulmao:        '/assets/vsm/pulmao.svg',
};

/** Mapa adicional para InventoryTriangle (tipo inventory) */
export const VSM_INVENTORY_PNG_MAP: Record<string, string> = {

  supermercado:  '/assets/vsm/supermercado.svg',
  fifo:          '/assets/vsm/fifo.svg',
  estoque:       '/assets/vsm/estoque.svg',
  pulmao:        '/assets/vsm/pulmao.svg',
};

// ─── Estilo compartilhado para imagens PNG ─────────────────────────────────
const imgStyle: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  objectFit: 'contain',
  pointerEvents: 'none',
};

// ─── Fallback SVGs para ícones sem PNG ────────────────────────────────────────

const VsmFallbackAutomatizado: React.FC<VsmShapeProps> = ({ width, height }) => (
  <svg width={width} height={height} viewBox="0 0 80 40" preserveAspectRatio="none"
    style={{ position: 'absolute', top: 0, left: 0 }}>
    <path d="M0 12 H54 V0 L80 20 L54 40 V28 H0 Z"
      fill="white" stroke="#1a1a1a" strokeWidth="1.5" strokeLinejoin="round" />
    <line x1="4" y1="20" x2="48" y2="20" stroke="#1a1a1a" strokeWidth="1.2" strokeDasharray="6,4" />
  </svg>
);

const VsmFallbackEmpilhador: React.FC<VsmShapeProps> = ({ width, height }) => (
  <svg width={width} height={height} viewBox="0 0 80 70" preserveAspectRatio="xMidYMid meet"
    style={{ position: 'absolute', top: 0, left: 0 }}>
    <rect x="12" y="8" width="44" height="38" rx="2" fill="white" stroke="#1a1a1a" strokeWidth="1.5" />
    <rect x="12" y="8" width="22" height="16" rx="1" fill="white" stroke="#1a1a1a" strokeWidth="1" />
    <line x1="56" y1="8" x2="56" y2="46" stroke="#1a1a1a" strokeWidth="2" />
    <line x1="56" y1="38" x2="78" y2="38" stroke="#1a1a1a" strokeWidth="2.5" strokeLinecap="round" />
    <line x1="56" y1="46" x2="78" y2="46" stroke="#1a1a1a" strokeWidth="2.5" strokeLinecap="round" />
    <circle cx="22" cy="60" r="6" fill="white" stroke="#1a1a1a" strokeWidth="1.5" />
    <circle cx="46" cy="60" r="6" fill="white" stroke="#1a1a1a" strokeWidth="1.5" />
  </svg>
);

const VsmFallbackCaminhao: React.FC<VsmShapeProps> = ({ width, height }) => (
  <svg width={width} height={height} viewBox="0 0 50 32" preserveAspectRatio="none"
    style={{ position: 'absolute', top: 0, left: 0 }}>
    <path d="M2 4 H35 V26 H2 Z" fill="white" stroke="#1a1a1a" strokeWidth="1.5" strokeLinejoin="round" />
    <path d="M35 12 H48 V26 H35 Z" fill="white" stroke="#1a1a1a" strokeWidth="1.5" strokeLinejoin="round" />
    <circle cx="10" cy="27" r="3.5" fill="#1a1a1a" />
    <circle cx="42" cy="27" r="3.5" fill="#1a1a1a" />
  </svg>
);

const VsmFallbackAviao: React.FC<VsmShapeProps> = ({ width, height }) => (
  <svg width={width} height={height} viewBox="0 0 80 60" preserveAspectRatio="xMidYMid meet"
    style={{ position: 'absolute', top: 0, left: 0 }}>
    <path d="M40 4 L68 36 L54 32 L50 54 L40 49 L30 54 L26 32 L12 36 Z"
      fill="white" stroke="#1a1a1a" strokeWidth="1.5" strokeLinejoin="round" />
  </svg>
);

const VsmFallbackBarco: React.FC<VsmShapeProps> = ({ width, height }) => (
  <svg width={width} height={height} viewBox="0 0 80 58" preserveAspectRatio="xMidYMid meet"
    style={{ position: 'absolute', top: 0, left: 0 }}>
    <path d="M8 36 Q40 56 72 36 L66 22 L14 22 Z"
      fill="white" stroke="#1a1a1a" strokeWidth="1.5" strokeLinejoin="round" />
    <rect x="28" y="7" width="24" height="15" rx="1" fill="white" stroke="#1a1a1a" strokeWidth="1" />
    <line x1="40" y1="1" x2="40" y2="7" stroke="#1a1a1a" strokeWidth="1.5" />
    <path d="M40 1 L56 7 L40 13 Z" fill="#1a1a1a" opacity="0.35" />
  </svg>
);

const VsmFallbackTrem: React.FC<VsmShapeProps> = ({ width, height }) => (
  <svg width={width} height={height} viewBox="0 0 80 65" preserveAspectRatio="none"
    style={{ position: 'absolute', top: 0, left: 0 }}>
    <rect x="6" y="4" width="68" height="44" rx="6" fill="white" stroke="#1a1a1a" strokeWidth="1.5" />
    <rect x="14" y="11" width="20" height="14" rx="1" fill="none" stroke="#1a1a1a" strokeWidth="1" />
    <rect x="46" y="11" width="20" height="14" rx="1" fill="none" stroke="#1a1a1a" strokeWidth="1" />
    <line x1="1" y1="56" x2="79" y2="56" stroke="#1a1a1a" strokeWidth="2" />
    <circle cx="21" cy="54" r="6" fill="white" stroke="#1a1a1a" strokeWidth="1.5" />
    <circle cx="59" cy="54" r="6" fill="white" stroke="#1a1a1a" strokeWidth="1.5" />
  </svg>
);

const VsmFallbackEmergencia: React.FC<VsmShapeProps> = ({ width, height }) => (
  <svg width={width} height={height} viewBox="0 0 40 34" preserveAspectRatio="xMidYMid meet"
    style={{ position: 'absolute', top: 0, left: 0 }}>
    <path d="M20 2 L38 32 H2 Z" fill="#1a1a1a" stroke="#1a1a1a" strokeWidth="1" strokeLinejoin="round" />
  </svg>
);

// Fallback SVGs para supermercado/fifo (usados pelo InventoryTriangle)
export const VsmShapeSupermercado: React.FC<VsmShapeProps> = ({ width, height }) => (
  <svg width={width} height={height} viewBox="0 0 40 40" preserveAspectRatio="xMidYMid meet"
    style={{ position: 'absolute', top: 0, left: 0 }}>
    <path d="M10 5 H35 V35 H10"
      fill="none" stroke="#1a1a1a" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
    <path d="M10 14 H28 M10 22 H28 M10 30 H28"
      stroke="#1a1a1a" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

export const VsmShapeFifo: React.FC<VsmShapeProps> = ({ width, height }) => (
  <svg width={width} height={height} viewBox="0 0 60 20" preserveAspectRatio="none"
    style={{ position: 'absolute', top: 0, left: 0 }}>
    <path d="M0 3 H60 M0 17 H60" stroke="#1a1a1a" strokeWidth="2" />
    <path d="M8 3 L14 17 M22 3 L28 17 M36 3 L42 17 M50 3 L56 17"
      stroke="#1a1a1a" strokeWidth="1" opacity="0.6" />
  </svg>
);

// ─── FALLBACK map para ícones sem PNG ─────────────────────────────────────────
const SVG_FALLBACKS: Record<string, React.FC<VsmShapeProps>> = {
  automatizado: VsmFallbackAutomatizado,
  empilhador:   VsmFallbackEmpilhador,
  caminhao:     VsmFallbackCaminhao,
  transporte:   VsmFallbackCaminhao,
  aviao:        VsmFallbackAviao,
  barco:        VsmFallbackBarco,
  trem:         VsmFallbackTrem,
  emergencia:   VsmFallbackEmergencia,
  supermercado: VsmShapeSupermercado,
  fifo:         VsmShapeFifo,
};

// Mapa de SVGs sketch originais (hand-drawn, preto/branco)
const VSM_SKETCH_MAP: Record<string, string> = {
  processo:     '/assets/vsm/sketch/processo.svg',
  estoque:      '/assets/vsm/sketch/estoque.svg',
  pulmao:       '/assets/vsm/sketch/pulmao.svg',
  supermercado: '/assets/vsm/sketch/supermercado.svg',
  externo:      '/assets/vsm/sketch/externo.svg',
  fornecedor:   '/assets/vsm/sketch/externo.svg',
  cliente:      '/assets/vsm/sketch/externo.svg',
  operador:     '/assets/vsm/sketch/operador.svg',
  entrega:      '/assets/vsm/sketch/entrega.svg',
  empurrado:    '/assets/vsm/sketch/empurrado.svg',
  puxado:       '/assets/vsm/sketch/puxado.svg',
  fifo:         '/assets/vsm/sketch/fifo.svg',
  databox:      '/assets/vsm/sketch/databox.svg',
  produto:      '/assets/vsm/sketch/produto.svg',
  info_eletro:  '/assets/vsm/sketch/info_eletro.svg',
  info_manual:  '/assets/vsm/sketch/info_manual.svg',
  kaizen:       '/assets/vsm/sketch/kaizen.svg',
  kanban_lote:  '/assets/vsm/sketch/kanban_lote.svg',
  kanban_prod:  '/assets/vsm/sketch/kanban_prod.svg',
  kanban_ret:   '/assets/vsm/sketch/kanban_ret.svg',
  kanban_posto: '/assets/vsm/sketch/kanban_posto.svg',
  kanban_sinal: '/assets/vsm/sketch/kanban_sinal.svg',
  nivelamento:  '/assets/vsm/sketch/nivelamento.svg',
  vaver:        '/assets/vsm/sketch/vaver.svg',
};

const BLUEPRINT_SKETCH_ICON_MAP: Record<string, string> = {
  operador: '/assets/vsm/sketch/operador.svg',
  transporte: '/assets/vsm/sketch/entrega.svg',
};

// ─── Custom scales an adjustments for specific PNGs ──────────────────────────────
const CUSTOM_PNG_STYLES: Record<string, React.CSSProperties> = {
  operador: {
    transform: 'scale(0.85)' // Diminuir 15% (ficando com 85% do tamanho)
  },
  kanban_prod: {
    height: '60%', // Ajustar para ficar aprox da mesma altura do kanban_lote
    marginTop: '20%' // Centralizar
  },
  kanban_ret: {
    height: '60%', // Ajustar para ficar aprox da mesma altura
    marginTop: '20%' // Centralizar
  }
};

export const VsmCanvasShape: React.FC<VsmShapeProps & { vsmIcon: string }> = ({
  vsmIcon, width, height, label, selected, color, isGhost
}) => {
  const { isDark, theme } = useTheme();
  const { vsmDesignStyle, vsmCardColor } = useProjectStore();
  const canvasBg = theme === 'black' ? '#131314' : isDark ? '#223244' : 'white';
  // Use the user-selected VSM color, with sensible fallbacks
  const userColor = vsmCardColor || (isDark ? '#7dd3fc' : '#1e3a5f');
  const isVerticalProduct = vsmIcon === 'produto' && height > width;
  const softBlueprintGlow = (glowColor: string, isSelected: boolean) => (
    isSelected
      ? `drop-shadow(0 0 1px ${glowColor}cc) drop-shadow(0 0 4px ${glowColor}55) drop-shadow(0 0 10px ${glowColor}2e)`
      : `drop-shadow(0 0 1px ${glowColor}88) drop-shadow(0 0 3px ${glowColor}30)`
  );
  const sketchSelectionFilter = [
    'drop-shadow(1px 0 0 #2563eb)',
    'drop-shadow(-1px 0 0 #2563eb)',
    'drop-shadow(0 1px 0 #2563eb)',
    'drop-shadow(0 -1px 0 #2563eb)',
    'drop-shadow(1px 1px 0 #2563eb)',
    'drop-shadow(-1px -1px 0 #2563eb)',
    'drop-shadow(1px -1px 0 #2563eb)',
    'drop-shadow(-1px 1px 0 #2563eb)',
  ].join(' ');

  // Sketch mode: usa SVGs hand-drawn originais (preto/branco, sem glow, sem gradiente)
  if (vsmDesignStyle === 'sketch') {
    // Kanban de Retirada sketch: pentágono + hachura diagonal / (igual ao SVG canônico)
    if (vsmIcon === 'kanban_ret') {
      const sketchInk = '#1a1a1a';
      const patternId = 'vsm-kanban-ret-hatch-sketch';
      return (
        <div style={{
          position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          pointerEvents: 'none',
          filter: selected ? sketchSelectionFilter : 'none',
        }}>
          <svg width={width} height={height} viewBox="0 0 88 66" preserveAspectRatio="none" style={{ display: 'block' }}>
            <defs>
              <pattern id={patternId} width="8" height="8" patternUnits="userSpaceOnUse">
                {/* Hachura única direção / (NE) */}
                <line x1="0" y1="8" x2="8" y2="0" stroke={sketchInk} strokeWidth="0.9" />
              </pattern>
            </defs>
            <path d="M 1.8 1.8 H 55.8 L 86.5 32.3 V 64 H 1.8 Z" fill={`url(#${patternId})`} stroke="none" />
            <path d="M 1.8 1.8 H 55.8 L 86.5 32.3 V 64 H 1.8 Z" fill="none" stroke={sketchInk} strokeWidth="1.6" strokeLinejoin="miter" vectorEffect="non-scaling-stroke" />
            <path d="M 55.8 1.8 V 32.3 H 86.5" fill="none" stroke={sketchInk} strokeWidth="1.6" strokeLinejoin="miter" vectorEffect="non-scaling-stroke" />
          </svg>
        </div>
      );
    }
    const sketchPath = VSM_SKETCH_MAP[vsmIcon];
    if (sketchPath) {
      // Nivelamento (OXOX): fundo opaco para mascarar a linha vsm_info tracejada
      // que passa por baixo do card (o card é ancorado sobre a linha).
      const sketchNivBg = vsmIcon === 'nivelamento' ? canvasBg : undefined;
      return (
        <div style={{
          position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          overflow: 'visible',
          pointerEvents: 'none',
          backgroundColor: sketchNivBg,
        }}>
          <img
            src={sketchPath}
            alt={vsmIcon}
            draggable={false}
            style={{
              position: isVerticalProduct ? 'absolute' : undefined,
              top: isVerticalProduct ? '50%' : undefined,
              left: isVerticalProduct ? '50%' : undefined,
              width: isVerticalProduct ? height : '100%',
              height: isVerticalProduct ? width : '100%',
              objectFit: 'fill',
              transform: isVerticalProduct
                ? 'translate(-50%, -50%) rotate(90deg)'
                : vsmIcon === 'nivelamento' ? 'scale(0.65)' : undefined,
              filter: selected ? sketchSelectionFilter : 'none',
              pointerEvents: 'none',
              display: 'block',
            }}
          />
          {/* Posto Kanban sketch: cartão kanban portrait (fold top-right), centralizado, topo da caixa */}
          {vsmIcon === 'kanban_posto' && (
            <svg
              viewBox="0 0 6 10"
              style={{ position: 'absolute', left: '22%', top: '8%', width: '56%', height: '48%', pointerEvents: 'none' }}
              overflow="visible"
            >
              <path d="M 0 0 L 4 0 L 6 2.5 L 6 10 L 0 10 Z" fill="white" stroke="#1a1a1a" strokeWidth="1.2" vectorEffect="non-scaling-stroke" />
              <polyline points="4,0 4,2.5 6,2.5" fill="none" stroke="#1a1a1a" strokeWidth="1.2" vectorEffect="non-scaling-stroke" />
            </svg>
          )}
        </div>
      );
    }
  }

  // V221 Technical: linhas pretas retas, sem glow, sem gradiente, sem hand-drawn (estilo engenharia)
  if (vsmDesignStyle === 'technical') {
    const techInk = isDark ? '#e5e7eb' : '#1a1a1a';
    // Posto Kanban: suporte em U (canônico) com cartão pendurado, ink técnico limpo
    if (vsmIcon === 'kanban_posto') {
      // Cartão kanban (portrait com dobra) centralizado dentro do U do posto
      const kx0 = 10, ky0 = -8, kw = 24, kh = 28, kfold = 7;
      const kPath = `M ${kx0} ${ky0} L ${kx0 + kw - kfold} ${ky0} L ${kx0 + kw} ${ky0 + kfold} L ${kx0 + kw} ${ky0 + kh} L ${kx0} ${ky0 + kh} Z`;
      return (
        <div style={{
          position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          pointerEvents: 'none',
        }}>
          <svg viewBox="0 0 43.867217 61.091263" width={width} height={height} preserveAspectRatio="xMidYMid meet" style={{ display: 'block' }}>
            {/* Contorno do posto kanban (suporte em U) */}
            <path
              d="m 4.1548582,58.391551 16.8330498,-0.38095 -0.064,-32.59343 L 3.1442582,24.877371 0.93924817,1.1259908 H 1.8176482 l 1.45941,0.0648 2.12621,22.1427702 16.4847198,0.19436 h 16.66217 l 2.31607,-22.67182022 h 1.23175 0.82824 l -2.30061,24.29120022 -17.70148,0.27839 0.39946,32.65022 16.69637,0.26997 0.30447,1.88928 H 22.093958 3.8634882 Z"
              fill={techInk}
              stroke={techInk}
              strokeWidth="1.6"
              strokeLinejoin="miter"
              vectorEffect="non-scaling-stroke"
            />
            {/* Cartão kanban produção (portrait com dobra no canto superior direito) */}
            <path d={kPath} fill={canvasBg} stroke={techInk} strokeWidth="1.2" vectorEffect="non-scaling-stroke" />
            <polyline points={`${kx0 + kw - kfold},${ky0} ${kx0 + kw - kfold},${ky0 + kfold} ${kx0 + kw},${ky0 + kfold}`} fill="none" stroke={techInk} strokeWidth="1.2" vectorEffect="non-scaling-stroke" />
          </svg>
        </div>
      );
    }
    // Operador: círculo + arco (mesma figura do blueprint, sem glow neon, ink preto)
    if (vsmIcon === 'operador') {
      return (
        <div style={{
          position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          pointerEvents: 'none',
          filter: selected ? sketchSelectionFilter : 'none',
        }}>
          <svg viewBox="0 0 56 64" width={width} height={height} fill="none" aria-hidden="true">
            <circle cx="28" cy="15" r="13.5" fill={canvasBg} stroke={techInk} strokeWidth="2" vectorEffect="non-scaling-stroke" />
            <path d="M3 31 C8 50 48 50 53 31" fill="none" stroke={techInk} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
          </svg>
        </div>
      );
    }
    // Nivelamento (Heijunka): reuso da geometria do blueprint, ink preto plano
    if (vsmIcon === 'nivelamento') {
      const nvScale = 0.65;
      const nvW = Math.round(width * nvScale);
      const nvH = Math.round(height * nvScale);
      const nvInsetL = Math.round((width - nvW) / 2);
      const nvInsetT = Math.round((height - nvH) / 2);
      const cellW = nvW / 4;
      const cy = nvH / 2;
      const pad = nvH * 0.12;
      const ry = nvH * 0.44 - pad;
      const rx = cellW * 0.34 - pad * 0.5;
      const xPad = cellW * 0.22;
      const sw = 1.4;
      // Nivelamento (technical) tem muitas linhas internas; o sketchSelectionFilter (8 drop-shadows)
      // gera um halo azul exagerado. Usa glow suave (mesma família do classicSelectedGlow).
      const nivSelectionFilter = 'drop-shadow(0 0 1px #2563eb) drop-shadow(0 0 2px #2563eb88)';
      return (
        <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none' }}>
          <div style={{ position: 'absolute', left: nvInsetL, top: nvInsetT, width: nvW, height: nvH, backgroundColor: canvasBg }} />
          <svg viewBox={`0 0 ${nvW} ${nvH}`} width={nvW} height={nvH} fill="none" style={{ position: 'absolute', left: nvInsetL, top: nvInsetT, filter: selected ? nivSelectionFilter : 'none' }}>
            <rect x="0.75" y="0.75" width={nvW - 1.5} height={nvH - 1.5} stroke={techInk} strokeWidth={sw} fill="none" vectorEffect="non-scaling-stroke" />
            {[1, 2, 3].map(i => (<line key={i} x1={i * cellW} y1="0" x2={i * cellW} y2={nvH} stroke={techInk} strokeWidth="0.8" vectorEffect="non-scaling-stroke" />))}
            <ellipse cx={cellW * 0.5} cy={cy} rx={rx} ry={ry} stroke={techInk} strokeWidth={sw} fill="none" vectorEffect="non-scaling-stroke" />
            <line x1={cellW * 1 + xPad} y1={cy - ry} x2={cellW * 2 - xPad} y2={cy + ry} stroke={techInk} strokeWidth={sw} vectorEffect="non-scaling-stroke" />
            <line x1={cellW * 2 - xPad} y1={cy - ry} x2={cellW * 1 + xPad} y2={cy + ry} stroke={techInk} strokeWidth={sw} vectorEffect="non-scaling-stroke" />
            <ellipse cx={cellW * 2.5} cy={cy} rx={rx} ry={ry} stroke={techInk} strokeWidth={sw} fill="none" vectorEffect="non-scaling-stroke" />
            <line x1={cellW * 3 + xPad} y1={cy - ry} x2={cellW * 4 - xPad} y2={cy + ry} stroke={techInk} strokeWidth={sw} vectorEffect="non-scaling-stroke" />
            <line x1={cellW * 4 - xPad} y1={cy - ry} x2={cellW * 3 + xPad} y2={cy + ry} stroke={techInk} strokeWidth={sw} vectorEffect="non-scaling-stroke" />
          </svg>
        </div>
      );
    }
    // Caminhão / Transporte: silhueta lateral limpa (cabine + baú + 2 rodas)
    if (vsmIcon === 'caminhao' || vsmIcon === 'transporte') {
      const techFill = vsmCardColor || (isDark ? '#cbd5e1' : '#cbd5e1');
      return (
        <div style={{
          position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          pointerEvents: 'none',
          filter: selected ? sketchSelectionFilter : 'none',
        }}>
          <svg width={width} height={height} viewBox="0 0 110 60" preserveAspectRatio="none" style={{ display: 'block' }}>
            {/* Baú de carga (esquerda) */}
            <rect x="2" y="8" width="64" height="38" fill={techFill} stroke={techInk} strokeWidth="1.6" strokeLinejoin="miter" vectorEffect="non-scaling-stroke" />
            {/* Cabine (direita) */}
            <path d="M 66 22 L 80 22 L 88 32 L 88 46 L 66 46 Z" fill={techFill} stroke={techInk} strokeWidth="1.6" strokeLinejoin="miter" vectorEffect="non-scaling-stroke" />
            {/* Janela da cabine */}
            <path d="M 70 25 L 78 25 L 83 32 L 70 32 Z" fill={canvasBg} stroke={techInk} strokeWidth="1.2" strokeLinejoin="miter" vectorEffect="non-scaling-stroke" />
            {/* Roda traseira */}
            <circle cx="18" cy="49" r="6" fill={canvasBg} stroke={techInk} strokeWidth="1.6" vectorEffect="non-scaling-stroke" />
            {/* Roda dianteira */}
            <circle cx="78" cy="49" r="6" fill={canvasBg} stroke={techInk} strokeWidth="1.6" vectorEffect="non-scaling-stroke" />
          </svg>
        </div>
      );
    }
    // Kanban de Retirada: pentágono (canto recortado superior-direito) + hachura diagonal /
    // Geometria fiel ao SVG canônico icones-vsm-blueprint/kanban-retirada.svg
    if (vsmIcon === 'kanban_ret') {
      const patternId = `vsm-kanban-ret-hatch-${isDark ? 'd' : 'l'}`;
      return (
        <div style={{
          position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          pointerEvents: 'none',
          filter: selected ? sketchSelectionFilter : 'none',
        }}>
          <svg width={width} height={height} viewBox="0 0 88 66" preserveAspectRatio="none" style={{ display: 'block' }}>
            <defs>
              <pattern id={patternId} width="8" height="8" patternUnits="userSpaceOnUse">
                {/* Hachura única direção / (NE) */}
                <line x1="0" y1="8" x2="8" y2="0" stroke={techInk} strokeWidth="0.9" />
              </pattern>
            </defs>
            {/* Hachura preenchendo o pentágono */}
            <path
              d="M 1.8 1.8 H 55.8 L 86.5 32.3 V 64 H 1.8 Z"
              fill={`url(#${patternId})`}
              stroke="none"
            />
            {/* Contorno do pentágono */}
            <path
              d="M 1.8 1.8 H 55.8 L 86.5 32.3 V 64 H 1.8 Z"
              fill="none"
              stroke={techInk}
              strokeWidth="1.6"
              strokeLinejoin="miter"
              vectorEffect="non-scaling-stroke"
            />
            {/* Linha do dobramento do canto recortado */}
            <path
              d="M 55.8 1.8 V 32.3 H 86.5"
              fill="none"
              stroke={techInk}
              strokeWidth="1.6"
              strokeLinejoin="miter"
              vectorEffect="non-scaling-stroke"
            />
          </svg>
        </div>
      );
    }
    // Demais ícones: reutiliza assets raster do blueprint (linhas limpas) via CSS mask em ink preto
    const blueprintSketchPath = BLUEPRINT_SKETCH_ICON_MAP[vsmIcon];
    if (blueprintSketchPath) {
      return (
        <div style={{
          position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          pointerEvents: 'none',
          filter: selected ? sketchSelectionFilter : 'none',
        }}>
          <div style={{
            width: '100%', height: '100%',
            backgroundColor: techInk,
            WebkitMaskImage: `url(${blueprintSketchPath})`,
            maskImage: `url(${blueprintSketchPath})`,
            WebkitMaskRepeat: 'no-repeat', maskRepeat: 'no-repeat',
            WebkitMaskPosition: 'center', maskPosition: 'center',
            WebkitMaskSize: '100% 100%', maskSize: '100% 100%',
          }} />
        </div>
      );
    }
    // Componentes vetoriais BpIcon: stroke preto contínuo, fill = vsmCardColor (ou cinza claro default)
    const BpIcon = VSM_BLUEPRINT_MAP[vsmIcon];
    if (BpIcon) {
      // V222: fill leve baseado na cor escolhida no menu Design; default cinza claro
      const techFill = vsmCardColor || (isDark ? '#cbd5e1' : '#cbd5e1');
      return (
        <div style={{
          position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: techInk,
          pointerEvents: 'none',
          filter: selected ? sketchSelectionFilter : 'none',
        }}>
          <BpIcon
            width={width}
            height={height}
            color={techInk}
            useGradient={false}
            sketchMode={false}
            fillColor={techFill}
            fillOpacityBoost={12.5}
          />
        </div>
      );
    }
  }

  // Blueprint: inline SVG com gradiente + glow neon
  if (vsmDesignStyle === 'blueprint') {
    if (vsmIcon === 'nivelamento') {
      // Heijunka box (OXOX) blueprint.
      // Renderiza a 65% do card (mesma escala do sketch), posicionado com os insets corretos
      // para que os handles do Card.tsx coincidam exatamente com a borda da figura.
      const glowFilter = softBlueprintGlow(userColor, selected);
      const nvScale = 0.65;
      const nvW = Math.round(width  * nvScale);
      const nvH = Math.round(height * nvScale);
      const nvInsetL = Math.round((width  - nvW) / 2);
      const nvInsetT = Math.round((height - nvH) / 2);
      // Geometria interna em coordenadas do viewBox (0 0 nvW nvH)
      // pad: margem interna para dar respiro entre shapes e borda do card
      const cellW = nvW / 4;
      const cy    = nvH / 2;
      const pad   = nvH * 0.12;          // respiro interno (~12% da altura)
      const ry    = nvH * 0.44 - pad;    // oval menor para não tocar a borda
      const rx    = cellW * 0.34 - pad * 0.5;
      const xPad  = cellW * 0.22;        // inset horizontal dos X
      const sw    = 1.5;
      return (
        <div style={{
          position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
          pointerEvents: 'none',
        }}>
          {/* Fundo opaco — bloqueia a seta tracejada vsm_info que passa por baixo */}
          <div style={{
            position: 'absolute',
            left: nvInsetL, top: nvInsetT,
            width: nvW, height: nvH,
            backgroundColor: canvasBg,
          }} />
          {/* Figura OXOX com glow neon */}
          <svg
            viewBox={`0 0 ${nvW} ${nvH}`}
            width={nvW}
            height={nvH}
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ position: 'absolute', left: nvInsetL, top: nvInsetT, filter: glowFilter }}
          >
            {/* Borda externa */}
            <rect x="0.75" y="0.75" width={nvW - 1.5} height={nvH - 1.5}
              stroke={userColor} strokeWidth={sw} fill="none" vectorEffect="non-scaling-stroke" />
            {/* Divisórias verticais (3) */}
            {([1, 2, 3] as const).map(i => (
              <line key={i} x1={i * cellW} y1="0" x2={i * cellW} y2={nvH}
                stroke={userColor} strokeWidth="0.8" vectorEffect="non-scaling-stroke" />
            ))}
            {/* O – célula 1 */}
            <ellipse cx={cellW * 0.5} cy={cy} rx={rx} ry={ry}
              stroke={userColor} strokeWidth={sw} fill="none" vectorEffect="non-scaling-stroke" />
            {/* X – célula 2 */}
            <line x1={cellW * 1 + xPad} y1={cy - ry} x2={cellW * 2 - xPad} y2={cy + ry}
              stroke={userColor} strokeWidth={sw} vectorEffect="non-scaling-stroke" />
            <line x1={cellW * 2 - xPad} y1={cy - ry} x2={cellW * 1 + xPad} y2={cy + ry}
              stroke={userColor} strokeWidth={sw} vectorEffect="non-scaling-stroke" />
            {/* O – célula 3 */}
            <ellipse cx={cellW * 2.5} cy={cy} rx={rx} ry={ry}
              stroke={userColor} strokeWidth={sw} fill="none" vectorEffect="non-scaling-stroke" />
            {/* X – célula 4 */}
            <line x1={cellW * 3 + xPad} y1={cy - ry} x2={cellW * 4 - xPad} y2={cy + ry}
              stroke={userColor} strokeWidth={sw} vectorEffect="non-scaling-stroke" />
            <line x1={cellW * 4 - xPad} y1={cy - ry} x2={cellW * 3 + xPad} y2={cy + ry}
              stroke={userColor} strokeWidth={sw} vectorEffect="non-scaling-stroke" />
          </svg>
        </div>
      );
    }

    if (vsmIcon === 'operador') {
      const neonOperatorColor = '#00FF85';
      const glowFilter = softBlueprintGlow(neonOperatorColor, selected);
      return (
        <div
          style={{
            position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            pointerEvents: 'none',
            filter: glowFilter,
          }}
        >
          <svg viewBox="0 0 56 64" width={width} height={height} fill="none" aria-hidden="true">
            <circle cx="28" cy="15" r="15.7" fill={`${neonOperatorColor}22`} stroke="none" />
            <path d="M8 31 C12 43 44 43 48 31 C41 36 15 36 8 31 Z" fill={`${neonOperatorColor}18`} stroke="none" />
            <path d="M3 31 C8 50 48 50 53 31" stroke={`${neonOperatorColor}33`} strokeWidth="6.4" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
            <circle cx="28" cy="15" r="13.5" fill={canvasBg} stroke={neonOperatorColor} strokeWidth="2.4" vectorEffect="non-scaling-stroke" />
            <circle cx="28" cy="15" r="11.2" fill="none" stroke={`${neonOperatorColor}70`} strokeWidth="1.4" vectorEffect="non-scaling-stroke" />
            <path d="M3 31 C8 50 48 50 53 31" stroke={neonOperatorColor} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
          </svg>
        </div>
      );
    }

    const blueprintSketchPath = BLUEPRINT_SKETCH_ICON_MAP[vsmIcon];
    if (blueprintSketchPath) {
      const glowFilter = softBlueprintGlow(userColor, selected);
      return (
        <div
          style={{
            position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            pointerEvents: 'none',
            filter: glowFilter,
          }}
        >
          <div
            style={{
              width: '100%',
              height: '100%',
              backgroundColor: userColor,
              WebkitMaskImage: `url(${blueprintSketchPath})`,
              maskImage: `url(${blueprintSketchPath})`,
              WebkitMaskRepeat: 'no-repeat',
              maskRepeat: 'no-repeat',
              WebkitMaskPosition: 'center',
              maskPosition: 'center',
              WebkitMaskSize: '100% 100%',
              maskSize: '100% 100%',
            }}
          />
        </div>
      );
    }

    const BpIcon = VSM_BLUEPRINT_MAP[vsmIcon];
    if (BpIcon) {
      const glowFilter = softBlueprintGlow(userColor, selected);
      return (
        <div
          style={{
            position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: userColor,
            filter: glowFilter,
            borderRadius: 2,
            pointerEvents: 'none',
          }}
        >
          <BpIcon
            width={width}
            height={height}
            color={userColor}
            useGradient={true}
            sketchMode={false}
          />
        </div>
      );
    }
  }

  // Em dark mode: invert(1) turns white fills→black, black outlines→white.
  // Then mix-blend-mode:screen makes black areas transparent (shows canvas bg color).
  const selectedGlow = 'drop-shadow(0 0 0px #2563eb) drop-shadow(0 0 1px #2563eb) drop-shadow(0 0 2px #2563eb)';
  const baseFilter = isDark ? 'invert(1)' : 'none';
  const combinedFilter = selected ? (isDark ? `invert(1) ${selectedGlow}` : selectedGlow) : baseFilter;
  const blendMode: React.CSSProperties['mixBlendMode'] = isDark ? 'screen' : 'normal';

  // 1. Se existe PNG, usar imagem
  const pngPath = VSM_PNG_MAP[vsmIcon];
  if (pngPath) {
    const customStyle = CUSTOM_PNG_STYLES[vsmIcon] || {};
    // nivelamento: fundo opaco para mascarar a linha de informação tracejada que passa atrás do card
    // (o card é ancorado sobre a linha; sem fundo, a linha sangra através do PNG transparente)
    const containerBg = canvasBg;
    return (
      <div style={{ position: 'relative', top: 0, left: 0, width: '100%', height: '100%', padding: 0, pointerEvents: 'none', backgroundColor: containerBg, isolation: 'isolate' }}>
        <img
          src={pngPath}
          alt={vsmIcon}
          draggable={false}
          style={{
            position: isVerticalProduct ? 'absolute' : undefined,
            top: isVerticalProduct ? '50%' : undefined,
            left: isVerticalProduct ? '50%' : undefined,
            width: isVerticalProduct ? height : '100%',
            height: isVerticalProduct ? width : '100%',
            objectFit: 'fill',
            transform: isVerticalProduct ? 'translate(-50%, -50%) rotate(90deg)' : undefined,
            filter: combinedFilter,
            mixBlendMode: blendMode,
            transition: 'filter 0.15s ease',
            ...customStyle
          }}
        />
        {/* Color tint overlay for sketch mode — mix-blend-mode:color tints without losing luminosity */}
        <div style={{
          position: 'absolute', inset: 0,
          background: userColor,
          mixBlendMode: 'color' as React.CSSProperties['mixBlendMode'],
          opacity: 0.55,
          pointerEvents: 'none',
        }} />
        {/* Posto Kanban: cartão kanban portrait (fold top-right), centralizado, topo da caixa */}
        {vsmIcon === 'kanban_posto' && (
          <svg
            viewBox="0 0 6 10"
            style={{ position: 'absolute', left: '22%', top: '8%', width: '56%', height: '48%', pointerEvents: 'none', mixBlendMode: blendMode }}
            overflow="visible"
          >
            <path d="M 0 0 L 4 0 L 6 2.5 L 6 10 L 0 10 Z" fill={canvasBg} stroke={isDark ? '#fff' : '#1a1a1a'} strokeWidth="1.2" vectorEffect="non-scaling-stroke" />
            <polyline points="4,0 4,2.5 6,2.5" fill="none" stroke={isDark ? '#fff' : '#1a1a1a'} strokeWidth="1.2" vectorEffect="non-scaling-stroke" />
          </svg>
        )}
      </div>
    );
  }

  // 2. Fallback SVG para ícones sem PNG
  const FallbackSVG = SVG_FALLBACKS[vsmIcon];
  if (FallbackSVG) {
    return <FallbackSVG width={width} height={height} label={label} selected={selected} color={userColor} isDark={isDark} />;
  }

  return null;
};
